import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Building2, Facebook, Globe, Instagram, Linkedin, MapPin, Phone, Star, Twitter, User } from 'lucide-react'
import DataResultsContainer from '../DataResultsContainer/DataResultsContainer.tsx'
import PeopleSearchWorkerModal from '../../../../PeopleSearch/PeopleSearchWorkerModal.tsx'
import { PeopleSearchProfile } from '../../../../../types/peopleSearch.ts'
import styles from '../SharedResults.module.scss'

interface Professional {
  id: string | number
  type?: 'PERSON' | 'COMPANY'
  name?: string
  last_name?: string
  razon_social?: string
  validated?: boolean
  image_profile?: string
  zone?: string
  stars?: number
  skill?: Array<{ id?: number; skill?: { id?: string | number; name?: string } }>
  skills?: Array<{ name?: string }>
  source?: 'profiles' | 'workers'
  displayName?: string
  profileType?: string
  email?: string
  phone?: string
  url_site_web?: string | null
  description?: string | null
  facebook?: string | null
  twitter?: string | null
  instagram?: string | null
  linkedin?: string | null
  unemployment_fund?: boolean
  current_job?: boolean
  identity_validated?: boolean | null
  employment_end_date?: string | null
  jobBoardZones?: Array<{
    id?: number
    city?: string
    jurisdiccion?: { id?: string | number; name?: string }
  }>
}

interface ProfessionalsListProps {
  professionals: Professional[]
  total: number
  noResultsMessage?: string
  openInNewWindow?: boolean
}

const ProfessionalsList: React.FC<ProfessionalsListProps> = ({ professionals, total, noResultsMessage, openInNewWindow = false }) => {
  const navigate = useNavigate()
  const [workerModalOpen, setWorkerModalOpen] = useState(false)
  const [selectedWorker, setSelectedWorker] = useState<PeopleSearchProfile | null>(null)

  const MAX_VISIBLE_ZONES = 2

  const mapWorkerSkills = useCallback((professional: Professional) => {
    const unique = new Map<number, string>()
    const skillArray = professional.skill ?? []
    skillArray.forEach((item: any) => {
      const entity = item?.skill ?? item
      if (!entity?.id) return
      const id = Number(entity.id)
      if (Number.isNaN(id)) return
      if (!unique.has(id)) {
        unique.set(id, String(entity.name ?? ''))
      }
    })
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }))
  }, [])

  const mapWorkerJurisdictions = useCallback((professional: Professional) => {
    const unique = new Map<number, string>()
    const zones = professional.jobBoardZones ?? []
    zones.forEach((zone: any) => {
      const entity = zone?.jurisdiccion ?? {}
      const id = Number(entity?.id ?? zone?.id ?? 0)
      if (Number.isNaN(id) || id === 0) return
      const name = String(entity?.name ?? zone?.city ?? '')
      if (name && !unique.has(id)) {
        unique.set(id, name)
      }
    })
    if (unique.size === 0 && professional.zone) {
      unique.set(0, professional.zone)
    }
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }))
  }, [])

  const getDisplayName = (professional: Professional): string => {
    if (professional.displayName) return professional.displayName
    if (professional.type === 'COMPANY') return professional.razon_social || 'Empresa'
    return `${professional.name || ''} ${professional.last_name || ''}`.trim() || 'Profesional'
  }

  const normalizeUrlForHref = (value: string | null | undefined): string | null => {
    if (!value) return null
    const trimmed = String(value).trim()
    if (!trimmed) return null
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  const getZoneLabel = (zone: NonNullable<Professional['jobBoardZones']>[number]): string => {
    const city = zone?.city?.trim()
    const province = zone?.jurisdiccion?.name?.trim()
    return city || province || ''
  }

  const getVisibleZones = (
    professional: Professional,
  ): { labels: string[]; remaining: number; allLabels: string[] } | null => {
    const zonesRaw = professional.jobBoardZones ?? []
    const zonesLabels = Array.from(
      new Set(
        zonesRaw
          .map((z) => getZoneLabel(z as NonNullable<Professional['jobBoardZones']>[number]))
          .filter((s) => Boolean(s))
      )
    )

    if (zonesLabels.length > 0) {
      const visible = zonesLabels.slice(0, MAX_VISIBLE_ZONES)
      const remaining = Math.max(0, zonesLabels.length - visible.length)
      return { labels: visible, remaining, allLabels: zonesLabels }
    }

    if (professional.zone) return { labels: [professional.zone], remaining: 0, allLabels: [professional.zone] }
    return null
  }

  const transformWorkerToProfile = useCallback((professional: Professional): PeopleSearchProfile => {
    const displayName = getDisplayName(professional)
    const skills = mapWorkerSkills(professional)
    const jurisdictions = mapWorkerJurisdictions(professional)

    return {
      id: String(professional.id),
      type: 'WORKER',
      displayName,
      avatarUrl: professional.image_profile ?? null,
      skills,
      jurisdictions,
      contact: {
        phone: professional.phone ?? null,
        email: professional.email ?? null,
        website: null,
      },
      tags: [],
      rating: null,
      portfolioCount: undefined,
      availability: professional.employment_end_date
        ? new Date(professional.employment_end_date) <= new Date()
          ? 'Disponibilidad inmediata'
          : `Disponible desde ${new Date(professional.employment_end_date).toLocaleDateString('es-AR')}`
        : null,
      unemploymentFund: Boolean(professional.unemployment_fund),
      hasIdentityValidated: Boolean(professional.identity_validated ?? false),
      rawType: 'WORKER',
      source: 'WORKER',
      raw: professional,
    }
  }, [mapWorkerSkills, mapWorkerJurisdictions])

  const handleOpenWorker = useCallback((professional: Professional) => {
    const workerProfile = transformWorkerToProfile(professional)
    setSelectedWorker(workerProfile)
    setWorkerModalOpen(true)
  }, [transformWorkerToProfile])

  const handleCloseWorker = useCallback(() => {
    setWorkerModalOpen(false)
    setSelectedWorker(null)
  }, [])

  const getSkillLabels = (professional: Professional): string[] => {
    const fromSkill = (professional.skill ?? [])
      .map((item) => item?.skill?.name)
      .filter(Boolean) as string[]

    const fromSkills = (professional.skills ?? [])
      .map((item) => item?.name)
      .filter(Boolean) as string[]

    return Array.from(new Set([...fromSkill, ...fromSkills])).slice(0, 2)
  }

  const getProfileType = (professional: Professional): string => {
    if (professional.profileType) return professional.profileType
    if (professional.source === 'workers') return 'Trabajador'
    if (professional.type === 'COMPANY') return 'Empresa'
    return 'Profesional'
  }

  return (
    <DataResultsContainer
      icon={<User size={18} />}
      title={`${professionals.length} ${professionals.length === 1 ? 'perfil encontrado' : 'perfiles encontrados'}`}
      subtitle={total > 3 ? `de ${total} totales` : undefined}
      color="green"
      total={total}
      maxDisplay={3}
      viewAllLink={`/buscar-perfiles`}
      viewAllText={`Ver los ${total} perfiles en Explorar Perfiles`}
      emptyMessage={noResultsMessage || "No se encontraron profesionales con esos criterios."}
      isEmpty={professionals.length === 0}
      openInNewWindow={openInNewWindow}
    >
      {professionals.slice(0, 3).map((professional) => {
        const displayName = getDisplayName(professional)
        const skillLabels = getSkillLabels(professional)
        const profileType = getProfileType(professional)
        const isCompany = professional.type === 'COMPANY'
        const isWorker = professional.profileType === 'Trabajador' || professional.source === 'workers'
        const visibleZones = getVisibleZones(professional)
        const description = professional.description ? String(professional.description).trim() : null
        const websiteHref = normalizeUrlForHref(professional.url_site_web)
        const phoneHref = professional.phone ? `tel:${professional.phone}` : null
        const facebookHref = normalizeUrlForHref(professional.facebook)
        const twitterHref = normalizeUrlForHref(professional.twitter)
        const instagramHref = normalizeUrlForHref(professional.instagram)
        const linkedinHref = normalizeUrlForHref(professional.linkedin)
        const hasExtras = Boolean(websiteHref || phoneHref || facebookHref || twitterHref || instagramHref || linkedinHref)

        return (
          <div key={`${professional.source}-${professional.id}`} className={`${styles.resultCard} ${styles.resultCardGreen}`}>
            <div className={styles.professionalCardHeader}>
              <div className={styles.professionalCardAvatar}>
                {professional.image_profile ? (
                  <img src={professional.image_profile} alt={displayName} />
                ) : (
                  isCompany ? <Building2 size={18} /> : <User size={18} />
                )}
              </div>
              <div className={styles.professionalCardInfo}>
                <span className={styles.resultCardTitle}>
                  {displayName}
                  {professional.validated && (
                    <span className={styles.verifiedBadge} title="Verificado">✓</span>
                  )}
                </span>
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeGreen}`}>{profileType}</span>
              </div>
            </div>

            {skillLabels.length > 0 && (
              <div className={styles.resultCardChips}>
                {skillLabels.map((label) => (
                  <span key={label} className={styles.resultCardChip}>{label}</span>
                ))}
              </div>
            )}

            <div className={styles.resultCardMeta}>
              {visibleZones && visibleZones.labels.length > 0 && (
                <div className={styles.resultCardLocation}>
                  <MapPin size={12} />
                  <span
                    className={styles.resultCardLocationText}
                    title={visibleZones.allLabels.join(', ')}
                  >
                    <span className={styles.resultCardLocationLabel}>Zonas de trabajo:</span>{' '}
                    {visibleZones.labels.join(', ')}
                    {visibleZones.remaining > 0 ? ` (+${visibleZones.remaining})` : ''}
                  </span>
                </div>
              )}
              {professional.stars !== undefined && professional.stars > 0 && (
                <span><Star size={12} /> {professional.stars.toFixed(1)}</span>
              )}
              {description && <p className={styles.resultCardDescription}>{description}</p>}
              {hasExtras && (
                <div className={styles.resultCardExtras}>
                  {websiteHref && (
                    <a
                      href={websiteHref}
                      target="_self"
                      rel="noreferrer"
                      className={styles.resultCardIconLink}
                      aria-label="Sitio web"
                    >
                      <Globe size={16} />
                    </a>
                  )}
                  {phoneHref && (
                    <a
                      href={phoneHref}
                      className={styles.resultCardIconLink}
                      aria-label="Teléfono"
                    >
                      <Phone size={16} />
                    </a>
                  )}
                  {facebookHref && (
                    <a
                      href={facebookHref}
                      target="_self"
                      rel="noreferrer"
                      className={styles.resultCardIconLink}
                      aria-label="Facebook"
                    >
                      <Facebook size={16} />
                    </a>
                  )}
                  {twitterHref && (
                    <a
                      href={twitterHref}
                      target="_self"
                      rel="noreferrer"
                      className={styles.resultCardIconLink}
                      aria-label="Twitter"
                    >
                      <Twitter size={16} />
                    </a>
                  )}
                  {instagramHref && (
                    <a
                      href={instagramHref}
                      target="_self"
                      rel="noreferrer"
                      className={styles.resultCardIconLink}
                      aria-label="Instagram"
                    >
                      <Instagram size={16} />
                    </a>
                  )}
                  {linkedinHref && (
                    <a
                      href={linkedinHref}
                      target="_self"
                      rel="noreferrer"
                      className={styles.resultCardIconLink}
                      aria-label="LinkedIn"
                    >
                      <Linkedin size={16} />
                    </a>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className={`${styles.resultCardCta} ${styles.resultCardCtaGreen}`}
              onClick={() => {
                if (isWorker) {
                  handleOpenWorker(professional)
                } else {
                  const url = `/perfil/${professional.id}`
                  if (openInNewWindow) {
                    window.open(`${window.location.origin}${url}`, '_blank')
                  } else {
                    navigate(url)
                  }
                }
              }}
            >
              {isWorker ? 'Ver detalles' : 'Ver perfil'} <ArrowRight size={14} />
            </button>
          </div>
        )
      })}
      <PeopleSearchWorkerModal
        isOpen={workerModalOpen}
        worker={selectedWorker}
        onClose={handleCloseWorker}
      />
    </DataResultsContainer>
  )
}

export default ProfessionalsList
